import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import {
  Button,
  ErrorState,
  Footnote,
  FormGroup,
  FormRow,
  Group,
  Icon,
  Notice,
  Row,
  SectionLabel,
  Skeleton,
} from '@store/ui';
import {
  api,
  ApiError,
  formatMoney,
  STARS_PAYMENT,
  toPaymentSelection,
  type BalanceResponse,
  type MoneyJson,
  type PaymentMethodOption,
  type PaymentSelection,
  type PaymentUrlKind,
  type ProductDetail,
  type QuoteResponse,
} from '../api';
import starsMark from '../assets/telegram-stars.webp';
import { LegalLinks } from '../legal';
import { haptic, openInvoice, webApp } from '../telegram';
import { fillScreen, ListSkeleton, useAsync } from './shared';

// --- Product ----------------------------------------------------------------

/**
 * A payment option's identity, as the server states it.
 *
 * Object identity will not do: `/checkout/methods` can be re-fetched and every
 * option comes back as a new object, which would drop the customer's choice
 * without anything on screen changing. The triple is what the server itself
 * distinguishes options by — one provider can offer two currencies.
 */
const methodKey = (option: PaymentMethodOption): string =>
  `${option.providerId}:${option.method}:${option.currency}`;

/**
 * The mark that stands in front of a payment method's name.
 *
 * Stars gets the star Telegram actually draws — the store's own copy of it —
 * and not the icon set's `star`, which is a monochrome outline in
 * `--text-tertiary` and is the same mark this app puts on a favourite and on a
 * rating. Those three things are unrelated, and only one of them is money. A
 * customer who has bought Stars anywhere else in Telegram has seen this
 * two-colour object on the invoice, in the balance and in the top-up sheet;
 * the grey outline was a different thing wearing its name.
 *
 * Decorative, and that is not a close call: it sits directly in front of the
 * words «Telegram Stars» in the same row, so a screen reader that announced it
 * would say the name twice. Empty `alt` *and* `aria-hidden`, which is what
 * `Icon` itself does for a glyph beside a label — the empty `alt` is what
 * removes it from the accessibility tree and the attribute says out loud that
 * this was decided rather than forgotten. It never stands alone: the only
 * place it is rendered is this row, under that title.
 *
 * `width`/`height` on the element, not only in CSS, so the 28px column is the
 * right shape before the image has decoded — the payment group sits directly
 * above the sticky bar and a row that resizes on decode moves the button.
 *
 * The other two are untouched, same glyphs at the same 20px: this change is
 * about Stars and nothing else.
 */
const methodMark = (option: PaymentMethodOption): ReactNode =>
  option.method === 'telegram_stars' ? (
    <img src={starsMark} alt="" aria-hidden width={20} height={20} decoding="async" />
  ) : (
    /* TON shares the wallet glyph with Crypto Pay: both hand the customer to
       something that holds coins. Drawing a TON logo would mean shipping a
       second two-colour asset for a rail that is off by default, and the icon
       set has no brand marks in it at all. */
    <Icon
      name={option.method === 'crypto' || option.method === 'ton' ? 'wallet' : 'card'}
      size={20}
    />
  );

/**
 * A payment row whose leading mark is a node rather than a glyph name.
 *
 * `Row` takes `icon?: IconName` and draws it through the icon set, so the only
 * leading marks it can carry are that set's monochrome, `currentColor` glyphs
 * — and the Stars mark is a two-colour picture, which is exactly the kind of
 * thing that set should not hold. Widening that prop is `packages/ui`'s to do
 * and this change does not own that file, so this is the smallest local
 * stand-in: the markup `Row` already emits for the shape these rows use — a
 * leading mark, a title over a subtitle, a tick when the row is a choice —
 * with the leading slot taking a node.
 *
 * Same class names, deliberately, so none of the geometry moves house: the
 * fixed 28px column, the hairline inset past it (`.rowitem--inset`) and the
 * 66px row are all `base.css`'s and stay that way. Putting the picture in the
 * *title* instead would have been the one-line version of this and is why it
 * was not done — the title is the body column, so the mark would have sat
 * where the text starts and the subtitle would have hung out to its left.
 *
 * See the report: it asks for `icon?: IconName | ReactNode` on `RowProps`, at
 * which point this goes and the rows go back to `Row`. `stars-asset.test.ts`
 * pins the class names against `Row`'s own until then, so the copy cannot
 * quietly drift from the original.
 */
function MethodRow({
  mark,
  title,
  subtitle,
  selected,
  onClick,
}: {
  readonly mark: ReactNode;
  readonly title: ReactNode;
  readonly subtitle: ReactNode;
  readonly selected?: boolean;
  readonly onClick?: () => void;
}) {
  const body = (
    <>
      <span className="rowitem__icon">{mark}</span>
      <span className="rowitem__body">
        <span className="rowitem__title">{title}</span>
        <span className="rowitem__subtitle">{subtitle}</span>
      </span>
      {selected ? (
        <span className="rowitem__check">
          {/* As in `Row`: on a button `aria-pressed` announces the state, so the
              tick is decoration; on a plain row nothing else would announce it. */}
          <Icon name="check" size={18} {...(onClick ? {} : { title: 'Выбрано' })} />
        </span>
      ) : null}
    </>
  );

  const className = 'rowitem rowitem--inset';

  if (!onClick) return <div className={className}>{body}</div>;

  return (
    <button
      type="button"
      className={className}
      onClick={onClick}
      {...(selected !== undefined ? { 'aria-pressed': selected } : {})}
    >
      {body}
    </button>
  );
}

/**
 * What the customer is choosing, named the way the next screen names it.
 *
 * «Криптовалюта» on its own is a category, not a method, and two rows can
 * answer to it. Tapping one hands the customer to CryptoBot, where the invoice,
 * the bot and the receipt are all branded Crypto Pay; tapping a direct TON row
 * opens the customer's own wallet. A row naming only the category would be
 * offering one thing and delivering whichever of them the operator happened to
 * enable, so every crypto row carries the name of the thing that opens. The Russian word
 * in front of it is what makes it a sentence rather than a logo.
 */
const methodTitle = (option: PaymentMethodOption): string =>
  option.method === 'telegram_stars'
    ? 'Telegram Stars'
    : option.providerId === 'crypto_pay'
        ? 'Крипто через Crypto Pay'
        : option.method === 'ton'
          ? 'Оплата в TON'
          : 'Другой способ';

/**
 * What choosing this row actually does, in one line — and always a line.
 *
 * The payment method API supplies both a provider-facing currency and its
 * customer-facing meaning. Stars gets the meaningful explanation; any future
 * non-Telegram method falls back to its configured currency rather than
 * exposing an internal provider identifier.
 *
 * Never `null`, and that is geometry rather than copy. These rows sit in one
 * group: a row without a subtitle is a 45px row next to 66px neighbours, and
 * the payment section is the last thing above the sticky bar, so a method whose
 * subtitle happened to be blank would shorten the page and move the button. The
 * type used to allow it and the JSX used to spread the prop conditionally —
 * both are gone, so no configuration can produce a short row.
 */
const methodSubtitle = (option: PaymentMethodOption): string =>
  option.method === 'telegram_stars'
    ? 'Оплата внутри Telegram'
    : option.providerId === 'crypto_pay'
        ? 'Откроется CryptoBot: USDT, TON'
        : option.method === 'ton'
          ? 'Перевод из вашего кошелька'
          : option.currency || 'Оплата вне Telegram';

/**
 * The Stars mark as it appears *inside a price*, sized off the text around it.
 *
 * `1em` and not a pixel count, because this one mark is set at two very
 * different sizes on this screen — `.price` is 17px and `.price--lg` is 34px —
 * and a fixed size would be wrong in at least one of them. Sizing off the font
 * means the mark is whatever the number beside it is, including if the customer
 * has scaled their text.
 *
 * The dimensions are CSS rather than `width`/`height` attributes for the same
 * reason they exist at all: the box is reserved before the image has loaded, so
 * nothing moves when it arrives. Attributes are absolute pixels and cannot
 * follow `em`, which is why this one differs from the payment row's mark.
 *
 * `-0.125em` is the optical seat. An inline image sits *on* the baseline, so at
 * 34px an unshifted square star stands a visible step above the digits it
 * follows — the descender room under a number is empty and the eye reads the
 * mark as floating. Dropping it an eighth of an em centres it against the
 * digits, which is where a currency mark belongs.
 */
const STARS_MARK: CSSProperties = {
  width: '1em',
  height: '1em',
  verticalAlign: '-0.125em',
};

/**
 * The currency whose mark this screen draws instead of setting.
 *
 * The literal, not an import: `@store/shared` is a declared dependency of this
 * app that no source file has ever pulled in, and reaching into a server-side
 * package for a three-letter string would put it in the customer's bundle. It
 * is the same literal `formatMoney` tests against in `api.ts`, and the two have
 * to agree or a Stars figure gets both marks or neither.
 *
 * `stars-amount.test.ts` is what holds them together: it names the currency
 * once and asserts both ends of it — that `money` draws the picture for it and
 * that `formatMoney` still sets the glyph for it — so whichever side drifted,
 * one of those two assertions is the one that fails.
 */
const STARS_CURRENCY = 'XTR';

/**
 * A money figure, for this screen only.
 *
 * `formatMoney` returns a `string` and half this app's tests assert on that
 * string, so it cannot hand back a picture — «56 ★» has to stay «56 ★» for
 * everything that reads a price as text. But the star in it is U+2605, a
 * typographic glyph in the body face, and this screen is the one place where a
 * Stars figure is the thing the customer is about to pay: the amount on the
 * «Итого» lines and on the pay button. Telegram draws its own two-colour star
 * for exactly this, the store now has that file, and a price that wears the
 * platform's mark next to a payment row wearing the same mark is one object
 * said twice rather than two things that happen to both be about Stars.
 *
 * Non-XTR is returned verbatim from `formatMoney` — same string, same spacing,
 * same thousands separators — so this can only ever change what a Stars figure
 * looks like. A rouble price is byte-for-byte what it was.
 *
 * `alt` carries the currency's name, and that is the opposite decision from
 * `methodMark`'s empty `alt` twenty lines up. There the row already said the
 * words «Telegram Stars» beside the mark and announcing it would have said the
 * name twice; here the mark *is* the currency and nothing else on the line
 * names it, so an empty `alt` would read the total out as a bare number with no
 * unit. On the pay button it is what makes the control announce as «Оплатить 56
 * Telegram Stars» rather than «Оплатить 56».
 *
 * The space is the one `formatMoney` puts there. It stays breakable because
 * `.price`, `.price--lg` and the button all set `white-space: nowrap` already —
 * the rule holding «1 990 ₽» together on one line is the rule that holds this
 * together, and there is no reason for the star to be pinned harder than the
 * currency symbol it replaces.
 */
export function money(value: MoneyJson): ReactNode {
  if (value.currency !== STARS_CURRENCY) return formatMoney(value);

  return (
    <>
      {value.amount}{' '}
      <img src={starsMark} alt="Telegram Stars" style={STARS_MARK} decoding="async" />
    </>
  );
}

/**
 * Whether a money figure is worth a line of its own.
 *
 * Guarded because the amount is a digit string off the wire and `BigInt` throws
 * on anything else — a malformed figure should hide a row, not blank the screen
 * the customer is buying from.
 */
const isPositive = (money: MoneyJson): boolean => {
  try {
    return BigInt(money.amount) > 0n;
  } catch {
    return false;
  }
};

/**
 * How to open a payment URL when the server did not say.
 *
 * The API declares `paymentUrlKind` and this client reads it; the floor exists
 * because the API and the Mini App ship separately, so a client can be newer
 * than the server it is talking to, and an older server sends the URL with no
 * kind at all. Refusing to open it then would break every payment on a store
 * that has not redeployed. Same three cases as `classifyPaymentUrl` in
 * `apps/api/src/routes/checkout.ts`, which keeps its own copy for reused
 * invoices — `t.me/$slug` is the only shape `openInvoice` accepts, everything
 * else on `t.me` is a deep link, and anything else leaves Telegram.
 */
function openerFor(declared: PaymentUrlKind | null, url: string): PaymentUrlKind {
  if (declared) return declared;

  try {
    const parsed = new URL(url);
    const host = parsed.hostname.toLowerCase();
    if (host !== 't.me' && host !== 'telegram.me') return 'external_link';
    return parsed.pathname.startsWith('/$') ? 'telegram_invoice' : 'telegram_link';
  } catch {
    return 'external_link';
  }
}

/**
 * Opens a Telegram deep link inside Telegram rather than a browser.
 *
 * `../telegram` exports `openInvoice` and no other opener, and that module is
 * not this change's to edit, so this is the smallest possible stand-in built on
 * the `webApp()` accessor it does export. It belongs there: the version gate
 * (`atLeast`) and the browser fallback are that module's job, and both are
 * approximated here. See the report — it asks for `openTelegramLink` and
 * `openExternalLink` wrappers so this can be deleted.
 *
 * Returns whether anything was actually opened. `openTelegramLink` reports no
 * outcome, so a truthy answer means "handed over", never "paid".
 */
function openTelegramLink(url: string): boolean {
  const app = webApp();
  if (app) {
    try {
      app.openTelegramLink(url);
      return true;
    } catch {
      // An older client throws `WebAppMethodUnsupported` rather than returning:
      // fall through to the browser, which is also the development path.
    }
  }
  return Boolean(window.open(url, '_blank', 'noopener'));
}

/** The last resort: a page outside Telegram. */
function openExternalLink(url: string): boolean {
  const app = webApp();
  if (app) {
    try {
      app.openLink(url);
      return true;
    } catch {
      // As above.
    }
  }
  return Boolean(window.open(url, '_blank', 'noopener'));
}

/**
 * The line box the total will occupy, held open before the figure exists.
 *
 * `.price--lg` is the display face — 34px set on a 41px line — and the
 * placeholder standing in for it was a bare 28px block, so the row was thirteen
 * pixels shorter while the price was being computed and the promo field, the
 * payment methods and the button all jumped down the instant the quote landed.
 * The skeleton keeps its own weight; the slot around it is the height of the
 * number that is coming.
 *
 * Every branch is now wrapped in it, not just the skeleton. The two payment
 * methods are priced in different currencies — «1 990 ₽» is 96px wide and
 * «2 388 ★» is 108px — so the trailing column used to be a different box in
 * every state, and «—» was a third width again. One slot, one box: the figure
 * is set into a container whose height is the line it is set on and whose width
 * cannot fall below the placeholder's, so nothing in the row moves when the
 * currency does.
 */
const TOTAL_SLOT = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  height: 41,
  minWidth: 96,
} as const;

/** The same idea one size down, for the balance line: `.price` is `--font-headline`,
    17px on a 22px line, and its placeholder is a 20px block. */
const PRICE_SLOT = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  height: 22,
  minWidth: 64,
} as const;

/**
 * The aside under the total, whose height must not depend on the price.
 *
 * Three different things are said in this one place — the terms of sale, the
 * reason a price could not be computed, and nothing at all — and each of them
 * used to bring its own box: a `Footnote` of one line, a `Footnote` of four, or
 * a `Notice` with 12px of padding and a border. The section is directly above
 * the promo field, the payment rows and the sticky bar, so every one of those
 * swaps moved all three.
 *
 * 36px is not a guess and not a lie about the content: it is two lines of
 * `--font-caption`, 13px set on an 18px line, and two lines is exactly what
 * «Оплачивая заказ, вы принимаете условия продажи и политику возврата.»
 * measures at 320px, 375px and 430px — every width this screen is checked at.
 * So in the state the screen spends its life in, the sentence fills the slot
 * with nothing left over; the shorter things borrow the rest. And it is a
 * `min-height`, so a longer sentence grows the slot rather than spilling out
 * of it — the reservation is a floor, not a cage.
 */
const TOTAL_ASIDE = { minHeight: 36 } as const;

/**
 * Room around the promo field when the browser scrolls it into view.
 *
 * Telegram floats its own header over the top of the WebView, and the scroller
 * has no `scroll-padding`, so focusing this input parked it directly under that
 * header — the field the customer is typing into, hidden behind chrome, which is
 * the "уезжает" everyone sees. Reserving the same insets the shell reserves at
 * the top, and the action bar's height at the bottom, keeps the field between
 * them. The durable fix is `scroll-padding` on `.app__scroll`; this covers the
 * one field where it costs a customer money.
 */
const PROMO_SCROLL_ROOM = {
  scrollMarginTop: 'calc(var(--safe-top) + var(--content-safe-top) + var(--space-6))',
  scrollMarginBottom: 'calc(var(--tap-target) + var(--space-6))',
} as const;

export function ProductScreen({
  shortId,
  onNavigate,
}: {
  shortId: string;
  onNavigate: (route: string) => void;
}) {
  const detail = useAsync<ProductDetail>(() => api.product(shortId), [shortId]);
  const methods = useAsync(() => api.paymentMethods(), []);
  /*
   * The balance, because the price depends on it.
   *
   * `/checkout/quote` only spends a balance that was asked for — `resolveBonus`
   * returns null for a null `bonusRequested` — and no client had ever asked, so
   * the discount ledger was unreachable from the shop. Asking for the whole
   * available figure is safe by design: the server caps it by the loyalty policy
   * and by the account under lock, and reports back what it actually took.
   */
  const wallet = useAsync<BalanceResponse>(() => api.balance(), []);
  const [variantId, setVariantId] = useState<string | null>(null);
  const [methodChoice, setMethodChoice] = useState<string | null>(null);
  const [quote, setQuote] = useState<QuoteResponse | null>(null);
  /*
   * Pricing is three states, and it used to be told as two.
   *
   * `quote === null` meant both "the request is in flight" and "there is no
   * price and there is not going to be one", and the screen rendered the first
   * reading for both: a pulsing skeleton in the total and a dead button saying
   * "Считаем цену…". Anything that stopped a price arriving therefore looked
   * exactly like a price arriving. Failure now carries its own message, so the
   * two cannot be confused again — and the button offers the way out.
   */
  const [quoteError, setQuoteError] = useState<string | null>(null);
  const [quoting, setQuoting] = useState(true);
  const [attempt, setAttempt] = useState(0);
  const [promo, setPromo] = useState('');
  const [appliedPromo, setAppliedPromo] = useState('');
  /*
   * The terms sentence, kept once it has been said.
   *
   * It arrives on the quote because that is the response that carries it, but
   * it is not about the quote: `checkout.termsNotice` is a fixed translation
   * and every quote for every method returns the same string. Held here it
   * stops being a thing that flickers — clearing the quote to re-price in
   * another currency used to blank the sentence and then put it back, two
   * repaints of the tallest aside on the screen for a fact that had not
   * changed.
   */
  const [terms, setTerms] = useState<string | null>(null);
  /*
   * The store's Stars price, as the server words it.
   *
   * Held next to `terms` and for the same reason: it is stated by a quote and
   * outlives it, so re-pricing does not blink it off and on. It is a fixed
   * store price, not a market rate — nothing here refreshes it.
   */
  const [rateNotice, setRateNotice] = useState<string | null>(null);
  /*
   * The verdict on the promo code, which outlives the quote that pronounced it.
   *
   * `promoRejection` is about the code the customer typed and not about the
   * price, but it used to be read straight off `quote` — so tapping a payment
   * row cleared the quote, the rejection line under the field vanished for as
   * long as the new price took, and the payment rows and the button rose 26px
   * and dropped back. Keyed by the code it was pronounced on, so it disappears
   * the moment the customer edits the field rather than lingering over a code
   * nobody has judged yet.
   */
  const [promoVerdict, setPromoVerdict] = useState<{
    code: string;
    rejection: QuoteResponse['promoRejection'];
  } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const product = detail.data?.product;
  const variants = detail.data?.variants ?? [];
  const selected = variants.find((variant) => variant.id === variantId) ?? variants[0] ?? null;

  /*
   * The chosen way to pay, which is an argument to the price and not a caption.
   *
   * The first offered option stands in until the customer picks, so the screen
   * always has a currency to quote in. A failed `/checkout/methods` leaves this
   * null and the selection falls back to Stars — the method every store has,
   * and what this screen assumed unconditionally before.
   */
  const options = methods.data?.methods ?? [];
  const chosen = options.find((option) => methodKey(option) === methodChoice) ?? options[0] ?? null;
  const chosenKey = chosen ? methodKey(chosen) : null;
  /*
   * No asset is sent. Crypto Pay's invoice lets the buyer pick the coin inside
   * CryptoBot, and the direct on-chain provider needs none because it only
   * ever takes TON.
   */
  const selection: PaymentSelection = chosen ? toPaymentSelection(chosen) : STARS_PAYMENT;

  /* Both of these are "we asked and got an answer", error included: a price
     must not be quoted in a currency we are about to replace, and it must not
     wait for ever on a request that already failed. */
  const methodsSettled = !methods.loading;
  const walletSettled = !wallet.loading;
  /* An empty list is an answer, and a different one from "we could not ask". */
  const noMethods = Boolean(methods.data) && options.length === 0;
  /*
   * Whether the section's notice is still true.
   *
   * The notice is shown only when the offered set is Stars-only. A broken or
   * future configuration must not cause a contradictory platform statement.
   */
  const starsOnly = options.every((option) => option.method === 'telegram_stars');

  const spendable = wallet.data?.available ?? null;
  const bonusRequest = spendable && isPositive(spendable) ? spendable.amount : null;
  /*
   * Whether this order has a balance line at all — a question for the wallet,
   * not for the quote.
   *
   * The row used to appear with the applied figure and leave with it, so a
   * whole 46px line arrived when the price did and left again on every method
   * switch, taking everything below it with it. What the row is *about* is
   * settled before any price is asked for: `/balance` answers once, the quote
   * effect waits for it (`walletSettled`), and it does not change for the life
   * of the screen. So the row's presence is decided once, and only the figure
   * inside it moves.
   *
   * Still nothing for an empty account: a "−0 ₽ с баланса" on a wallet holding
   * nothing would be an advert for a feature, which is what the row was kept
   * out of before.
   */
  const spendsBalance = bonusRequest !== null;
  /* The promo verdict, matched to the code it was pronounced on. */
  const promoRejection =
    promoVerdict && promoVerdict.code === appliedPromo ? promoVerdict.rejection : null;

  /*
   * The code reaches the server a beat after the typing stops.
   *
   * Every keystroke used to be its own quote request, so a six-character promo
   * opened six of them and each answer could add or remove the rejection line
   * under the field — the page shifting under a thumb that is still typing,
   * with the keyboard up. Trimmed here as well: `/quote` validates the code
   * with `.trim().min(1)`, so one stray space was a 400 that took the whole
   * price down with it.
   */
  useEffect(() => {
    const code = promo.trim();
    if (code === appliedPromo) return;
    const timer = setTimeout(() => setAppliedPromo(code), 400);
    return () => clearTimeout(timer);
  }, [promo, appliedPromo]);

  useEffect(() => {
    if (!product) return;
    /* Nothing can be priced before the currency and the balance are known: a
       quote raised now would be replaced a moment later, and the customer would
       watch the total change by itself. */
    if (!methodsSettled || !walletSettled) return;

    if (noMethods) {
      /* There is no method, so there is no price — and saying so is the whole
         point. Left "quoting", this screen pulses a skeleton for ever at
         somebody who has already been told, one section below, that the shop
         cannot take their money right now. */
      setQuote(null);
      setQuoteError(null);
      setQuoting(false);
      return;
    }

    let cancelled = false;

    setQuoting(true);
    setQuoteError(null);

    const ask = (bonusAmount: string | null) =>
      api.quote({
        productId: product.id,
        /*
         * No variants is a shape, not an unfinished choice. The server prices
         * the product itself when `variantId` is null — `/api/checkout/quote`
         * takes `variantId: body.variantId ?? null` — but this effect used to
         * open with a guard that returned whenever nothing was selected, and
         * `selected` is null exactly when the product has no variants. Those
         * products never asked for a price at all: no request, so no answer, so
         * no failure either, and a button that said "Считаем цену…" until the
         * app was closed. Nobody could buy them.
         */
        variantId: selected?.id ?? null,
        promoCode: appliedPromo || null,
        bonusAmount,
        payment: selection,
      });

    void (async () => {
      try {
        let result: QuoteResponse;
        try {
          result = await ask(bonusRequest);
        } catch (cause) {
          if (!bonusRequest) throw cause;
          /*
           * A balance must never be the reason a product cannot be bought.
           *
           * The server refuses a quote it cannot charge for at all — a balance
           * that swallows the whole price leaves nothing to invoice and there
           * is no zero-payment path — and it refuses a spend against an account
           * that has since moved. Both come back as a plain 400, and both used
           * to be survivable: the same basket prices fine without the bonus. So
           * ask again for the price the customer can definitely pay, and let
           * the missing «с баланса» line be the only trace.
           */
          result = await ask(null);
        }
        if (cancelled) return;
        setQuote(result);
        /* Both of these are stated by the quote and outlive it — see their
           declarations. Written here, where a fresh answer exists, and nowhere
           else, so neither can drift from the last thing the server said. */
        setTerms(result.notices.terms);
        setRateNotice(result.notices.rate);
        setPromoVerdict({ code: appliedPromo, rejection: result.promoRejection });
        setQuoting(false);
      } catch (cause) {
        if (cancelled) return;
        /* The old amount goes out with the failed request: it was priced under
           conditions the server has just refused, and a button offering to
           charge it is a promise this screen can no longer keep. */
        setQuote(null);
        setQuoteError(cause instanceof ApiError ? cause.message : 'Не удалось рассчитать цену');
        setQuoting(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    product?.id,
    selected?.id,
    appliedPromo,
    attempt,
    methodsSettled,
    walletSettled,
    noMethods,
    chosenKey,
    bonusRequest,
  ]);

  /*
   * A new attempt number, because nothing else changed: the request failed for
   * a reason that is not in the dependencies, so this is what makes "try it
   * again" a state change React can see.
   *
   * The other two writes are what make the tap visible. The effect that clears
   * the failure runs after the paint, so on the bare version the frame after
   * the press was identical to the frame before it — same warning, same
   * «Рассчитать заново» — and the only way to find out whether the retry had
   * started was to wait and see. Clearing the failure here puts the skeleton
   * and «Считаем цену…» up on the tap instead. The button is disabled across
   * that gap (`!quote && !quoteFailed`), so flipping its action back to `buy`
   * cannot fire a purchase off a price that does not exist yet.
   */
  const retryQuote = () => {
    setQuoteError(null);
    setQuoting(true);
    setAttempt((previous) => previous + 1);
  };

  /**
   * Hands an order back when it will never be paid.
   *
   * An order holds the stock and the balance it spent for the whole reservation
   * window. When the invoice cannot be raised, that hold is against a customer
   * who has nothing to show for it — their own last key, reserved away from
   * them for half an hour by a button that failed. Best effort by definition:
   * if the release also fails the expiry job still runs, and telling somebody
   * their failed payment also failed to be undone helps nobody.
   */
  const release = async (orderId: string) => {
    try {
      await api.cancelOrder(orderId);
    } catch {
      // Deliberately silent — see above.
    }
  };

  const buy = async () => {
    if (!quote) return;
    setBusy(true);
    setError(null);

    /* Held only while this screen is still responsible for the order: cleared
       the moment the invoice is open, because from then on the order screen
       owns it and cancelling it from here would take it out from under a
       payment that may already be going through. */
    let unpaid: string | null = null;

    try {
      haptic.impact('medium');
      const { order } = await api.createOrder(quote.quote.id);
      unpaid = order.id;

      /* The same selection the quote was priced with. Quoting in one currency
         and invoicing in another is a different price. */
      const payment = await api.createPayment(order.id, selection);

      if (!payment.paymentUrl) {
        await release(order.id);
        unpaid = null;
        setError('Не удалось создать счёт. Попробуйте ещё раз.');
        return;
      }

      const kind = openerFor(payment.paymentUrlKind, payment.paymentUrl);
      unpaid = null;

      if (kind === 'telegram_invoice') {
        /* Stars, and only Stars: `openInvoice` refuses anything that is not a
           `t.me/$slug`, and it is the one opener that reports an outcome. */
        const status = await openInvoice(payment.paymentUrl);
        if (status === 'paid') {
          haptic.success();
          onNavigate(`/orders/${order.id}`);
        } else if (status === 'failed') {
          haptic.error();
          setError('Оплата не прошла. Деньги не списаны.');
        } else {
          onNavigate(`/orders/${order.id}`);
        }
        return;
      }

      const opened =
        kind === 'telegram_link'
          ? openTelegramLink(payment.paymentUrl)
          : openExternalLink(payment.paymentUrl);

      if (!opened) {
        /* A blocked popup, and the order is untouched: the invoice exists and
           the order screen holds its URL, so this goes forward rather than
           back. No message with it — the screen it would be written on is the
           one being left. */
        haptic.error();
        onNavigate(`/orders/${order.id}`);
        return;
      }

      /*
       * Neither of these two openers reports anything back, so from here the
       * outcome is genuinely unknown. Claiming success would be a lie and
       * staying put would strand the customer, so go to the order — that screen
       * polls the real status and is the only thing that knows.
       */
      onNavigate(`/orders/${order.id}`);
    } catch (cause) {
      if (unpaid) await release(unpaid);
      haptic.error();
      setError(cause instanceof ApiError ? cause.message : 'Не удалось оформить заказ');
    } finally {
      setBusy(false);
    }
  };

  /*
   * The skeleton is held until the screen's *shape* is known, not just its text.
   *
   * Three requests go out together at mount and only one of them was waited
   * for. The other two do not fill something in — they decide whether a node
   * exists at all:
   *
   *   - `/me/balance` decides `spendsBalance`, and the «С баланса» row is
   *     rendered only when it is true. Measured at 375×812: the row arriving
   *     took the promo field and «Как активировать» down by 46px.
   *   - `/checkout/methods` decides `methods.data`, and the whole «Оплата»
   *     section is `methods.data ? … : null`. Same measurement: +161px, and a
   *     fifth section appearing under a thumb already resting on the screen.
   *
   * Everything the quote later changes is already held in a slot of the size
   * that is coming — `TOTAL_SLOT`, `PRICE_SLOT`, `TOTAL_ASIDE`, the
   * hard-space footnote — which is why those arrivals cost nothing. A slot
   * cannot be the answer here: reserving a balance row and then removing it for a wallet that
   * holds nothing would be the same jump upside down, and the row is
   * deliberately absent for an empty account.
   *
   * So the shape is settled before the first paint instead. The three requests
   * are already in flight in parallel, so this waits for the slowest of them
   * rather than for their sum, and `detail` was being waited for anyway.
   * `!== false` rather than `.loading`: a request that failed has settled too,
   * and a screen that hung on a failed `/balance` would never paint at all.
   */
  const shapeSettled = !detail.loading && methodsSettled && walletSettled;

  if (!shapeSettled) {
    return (
      <div className="container stack">
        <Skeleton height={28} width="70%" />
        <ListSkeleton rows={2} />
      </div>
    );
  }

  if (detail.error || !product) {
    return (
      <div className="container">
        <div style={fillScreen}>
          <ErrorState
            title="Товар недоступен"
            text={detail.error ?? undefined}
            onRetry={detail.reload}
          />
        </div>
      </div>
    );
  }

  /* Stock is on the variant when there is one and on the product when there is
     not. Reading only the variant made `soldOut` false for every product
     without variants — harmless while none of them could be bought at all, and
     an unbuyable product with a live "Оплатить" button the moment they could. */
  const soldOut = (selected ? selected.available : product.available) === 0;
  /* Failed, as opposed to still going: a quote in flight after a failure has
     already cleared the message, so this is only ever "we asked and were told
     no". */
  const quoteFailed = quoteError !== null && quote === null;
  /* No price is coming, for either of the two reasons there can be: it was
     refused, or there is nothing to price it in. Both must stop the skeleton. */
  const noPrice = quoteFailed || noMethods;
  /*
   * The balance the server actually took off this order, in the store's own
   * currency. Not what the account holds and not what was asked for: the cap is
   * the server's to apply, and this is the figure it applied — so the line reads
   * «−150 ₽ с баланса» only when 150 ₽ genuinely came off.
   */
  const bonusApplied = quote?.quote.breakdown.bonusApplied ?? null;
  const balanceLine = bonusApplied && isPositive(bonusApplied) ? bonusApplied : null;

  return (
    <div className="container stack">
      <div>
        <h1 className="title">{product.title}</h1>
        {product.subtitle ? <p className="muted">{product.subtitle}</p> : null}
      </div>

      {product.description ? <p className="text">{product.description}</p> : null}

      {variants.length > 0 ? (
        <section>
          <SectionLabel>Вариант</SectionLabel>
          <Group>
            {variants.map((variant) => (
              <Row
                key={variant.id}
                title={variant.title}
                {...(variant.durationDays ? { subtitle: `${variant.durationDays} дней` } : {})}
                trailing={<span className="price">{money(variant.price)}</span>}
                selected={variant.id === selected?.id}
                onClick={() => {
                  haptic.select();
                  setVariantId(variant.id);
                }}
              />
            ))}
          </Group>
        </section>
      ) : null}

      {/*
       * The total is a group of lines, not a hand-built card of `.row--between`
       * pairs: the same hairline that separates the order's items separates
       * price from balance from total, so the two screens rhyme. The terms of
       * sale are an aside about the figure above them, which is what a footnote
       * is — they used to be a lozenge floating loose in the stack.
       *
       * Three lines, and the *number* of them is fixed before any price exists:
       * price, balance-if-the-wallet-has-one, total. Only the figures inside
       * them change, and each figure sits in a slot the size of the figure that
       * is coming. That is what makes the section the same height in all five
       * states it has — asking, priced in ₽, priced in ★, refused, re-asking.
       */}
      <section>
        <SectionLabel>Итого</SectionLabel>
        <Group>
          <Row
            title="Цена"
            /* The selected variant's price, not the product's: with variants on
               screen, picking the twelve-month one left this line showing the
               base price and only the total below it moved. */
            trailing={<span className="price">{money(selected?.price ?? product.price)}</span>}
          />
          {/*
           * "-150 руб с баланса и остается оплатить 140", as its own line.
           *
           * Present when the account has something to spend — see
           * `spendsBalance` — and not when a quote happens to be on screen.
           * Nothing here is computed: the figure is the one the order will be
           * frozen with, and the row above and below it are the server's too.
           * A dash while the server is still deciding would claim it had
           * decided, so the placeholder pulses like the total's.
           */}
          {spendsBalance ? (
            <Row
              title="С баланса"
              trailing={
                <span style={PRICE_SLOT}>
                  {balanceLine ? (
                    <span className="price">−{money(balanceLine)}</span>
                  ) : quote || noPrice ? (
                    /* Asked for and not granted — the server caps the spend, and
                       this is it saying "none of it". */
                    <span className="price">—</span>
                  ) : (
                    <Skeleton height={20} width="64px" />
                  )}
                </span>
              }
            />
          ) : null}
          <Row
            title={<strong>К оплате</strong>}
            trailing={
              <span style={TOTAL_SLOT}>
                {quote ? (
                  <span className="price--lg">{money(quote.quote.finalAmount)}</span>
                ) : noPrice ? (
                  /* A dash in the same face as the figure: there is no amount,
                     and a placeholder that keeps pulsing insists one is coming. */
                  <span className="price--lg">—</span>
                ) : (
                  <Skeleton height={28} width="96px" />
                )}
              </span>
            }
          />
        </Group>
        {/*
         * One slot, three things it can say, one height — see `TOTAL_ASIDE`.
         *
         * What it does not say is «Цена действительна до …». The expiry is a
         * server-side lock on a figure this screen re-asks for whenever
         * anything on it moves, so announcing a deadline the customer cannot
         * miss only invites them to hurry. Still on the wire; not repeated.
         *
         * The store's Stars price is not here either, but it is not gone: it
         * belongs under the method it describes, so it sits below the payment
         * group and only when Stars is the selected rail.
         */}
        <div style={TOTAL_ASIDE}>
          {quoteFailed ? (
            /*
             * The reason, where the figure should have been — as a caption, not
             * a lozenge. `Notice` is for a blocking condition: the one further
             * down, «нет доступных способов оплаты», keeps its box because the
             * order genuinely cannot be paid. This one is retryable, the button
             * below it offers the retry, and the colour carries the failure
             * without a border — the same reasoning the promo field's own error
             * already follows. Footnote metrics, so the swap costs no height.
             */
            <p className="footnote" role="alert" style={{ color: 'var(--danger)' }}>
              {quoteError}
            </p>
          ) : terms ? (
            /*
             * The terms sentence lives here, under the figure it is about, and
             * no longer inside the sticky bar.
             *
             * In the bar it was the only thing whose presence depended on a
             * quote, so the bar's own height did: selecting a sold-out variant
             * took two lines away, the bar lost 57px, and a customer standing at
             * the bottom of the page was thrown 48px up as the scroll clamped to
             * the shorter content. Both numbers measured at 375×812, both still
             * zero — the bar is padding plus a 44px button in every state there
             * is. Read from `terms` and not from `quote.notices.terms`, so that
             * clearing the quote to re-price in another currency cannot blank
             * it: every quote carries the same sentence either way.
             */
            <Footnote>{terms}</Footnote>
          ) : (
            /* Before the first quote there is nothing to say here, and the slot
               is held open rather than left to collapse — otherwise everything
               below it slid down by two caption lines the moment a price first
               landed. */
            <Footnote>{'\u00a0'}</Footnote>
          )}
        </div>
      </section>

      {/* The row's own label does the work an overline used to do above a bare
          input, and the group gives the field a surface to sit in. */}
      <section>
        <FormGroup>
          <FormRow label="Промокод" htmlFor="promo">
            <input
              id="promo"
              className="input"
              style={PROMO_SCROLL_ROOM}
              value={promo}
              onChange={(event) => setPromo(event.target.value)}
              placeholder="Если есть"
              aria-invalid={Boolean(promoRejection)}
              {...(promoRejection ? { 'aria-describedby': 'promo-error' } : {})}
            />
          </FormRow>
        </FormGroup>
        {promoRejection ? (
          /* An inline field error rather than a boxed lozenge: it belongs to
             the field above it exactly the way a footnote belongs to a group,
             and the colour already carries the failure without the box.
             Read from `promoVerdict` rather than from the quote, so that
             re-pricing in another currency does not blink it off and on. */
          <p
            id="promo-error"
            role="alert"
            className="caption"
            style={{
              color: 'var(--danger)',
              margin: 'var(--space-2) 0 0',
              padding: '0 var(--space-4)',
            }}
          >
            {promoRejection.message}
          </p>
        ) : null}
      </section>

      {product.activationInstructions ? (
        <section>
          <SectionLabel>Как активировать</SectionLabel>
          <Group padded>
            <p className="text">{product.activationInstructions}</p>
          </Group>
        </section>
      ) : null}

      {/* Eligibility is decided by the server; the client only renders the
          available methods and never treats a disabled rail as a visual choice. */}
      {methods.data ? (
        <section>
          <SectionLabel>Оплата</SectionLabel>
          {noMethods ? (
            /* The one aside on this screen that keeps its box: with no method
               the order cannot be paid at all, so it is a blocking condition
               rather than a note about one. */
            <Notice tone="warning">Сейчас нет доступных способов оплаты. Попробуйте позже.</Notice>
          ) : (
            <Group>
              {options.map((option) => {
                const key = methodKey(option);
                /* One method is not a choice. Given a tick and a tap target it
                   reads as one, and tapping it does nothing at all — so it stays
                   a statement of how this will be paid. */
                const pickable = options.length > 1;

                return (
                  <MethodRow
                    key={key}
                    mark={methodMark(option)}
                    title={methodTitle(option)}
                    /* Unconditional, and `methodSubtitle` never returns null —
                       every row in this group is a title over a subtitle, so
                       they are all the same height and the group's height does
                       not depend on which methods the store has configured. */
                    subtitle={methodSubtitle(option)}
                    {...(pickable
                      ? {
                          selected: key === chosenKey,
                          onClick: () => {
                            haptic.select();
                            /*
                             * The whole pricing state goes, not just the figure.
                             *
                             * The total on screen is in the currency being left
                             * behind, so dropping it puts the placeholder back
                             * for the moment the new price takes rather than
                             * showing 1990 ★ on a row that now says roubles.
                             *
                             * The failure has to go with it, and that is the
                             * part this was missing. `quoteError` outlives the
                             * method it was raised for, and the effect that
                             * clears it runs *after* the paint — so a method
                             * nobody had tried yet got one frame rendering the
                             * previous one's warning over a «Рассчитать заново»
                             * button, which on that frame would only re-ask the
                             * question the customer had already answered by
                             * tapping. All four writes are one batch, so the
                             * first frame after the tap is the honest one:
                             * placeholder, no warning, «Считаем цену…».
                             */
                            setQuote(null);
                            setQuoteError(null);
                            setQuoting(true);
                            setMethodChoice(key);
                          },
                        }
                      : {})}
                  />
                );
              })}
            </Group>
          )}
          {/*
           * Not under the warning: "оплата проходит внутри Telegram, товар
           * придёт в чат" describes a purchase that cannot happen, directly
           * below the line saying so.
           *
           * And not under a crypto row either. The notice says this store is
           * paid in Stars, which stops being true the moment a second rail is
           * listed directly above it — and the customer can see that it is not
           * true. It describes a Stars-only store and is shown there and nowhere
           * else; `starsOnly` is that test.
           */}
          {methods.data.notice && !noMethods && starsOnly ? (
            <Footnote>{methods.data.notice}</Footnote>
          ) : null}
          {/*
           * The store's Stars price, and only where it applies.
           *
           * Shown when Stars is the selected method and not otherwise: it says
           * what a rouble costs in Stars, which is not a fact about Crypto Pay
           * or about a TON transfer, and printing it under either would be
           * describing a conversion that is not happening.
           *
           * Always rendered, never conditionally present. The line is the last
           * thing above the sticky bar and the selection changes at a tap, so a
           * footnote that came and went would move the pay button under the
           * thumb reaching for it — the defect `checkout-layout.test.ts` exists
           * about. One line either way: the rate when it applies, a hard space
           * when it does not.
           *
           * `\u00a0` and not an empty string, because an empty `Footnote`
           * collapses to nothing and that is the same shift by another route.
           */}
          <Footnote>
            {selection.method === 'telegram_stars' && rateNotice ? rateNotice : '\u00a0'}
          </Footnote>
        </section>
      ) : null}

      {/*
       * The terms of the sale, where the sale is being made.
       *
       * A customer decides to pay on this screen, not on the home screen, and a
       * reviewer checking the store looks for the documents next to the price.
       * The footnote above the total already says the purchase is subject to
       * them; this is where they can actually be read.
       *
       * Above the bar and never below it. `.action-bar` is
       * `position: sticky; bottom: 0` and `base.css` gives the screen's last
       * child `margin-bottom: 0` only when that child *is* the bar — a sibling
       * after it would unpin both, which is the defect the first round of
       * `checkout-layout.test.ts` was written about.
       *
       * `LegalLinks` is `null` unless an address is configured, and the answer
       * is a build-time constant: it cannot arrive late and move the button.
       */}
      <LegalLinks />

      <div className="action-bar">
        {/* The failure and the control that answers it are one unit. Rendered
            above the bar, as this was, the sentence explaining why the payment
            did not go through can be scrolled away from the button the customer
            is about to press again. */}
        {error ? (
          <div style={{ marginBottom: 'var(--space-3)' }}>
            <Notice tone="warning">{error}</Notice>
          </div>
        ) : null}
        {/*
         * Four states, and each one says what it is.
         *
         * The button used to have two labels for three situations, so "we are
         * working on it" and "this will never finish" were the same sentence on
         * the same dead button. A price that failed now offers to be computed
         * again, and the reason is on screen above the total.
         *
         * One line, always. The label is the last thing inside the bar whose
         * length is not fixed — «Считаем цену…» is 13 characters and «Оплатить
         * 12 345,678901 USDT» is 27 — and `.button` sets no `white-space`, so a
         * label long enough to wrap would make the bar 21px taller, which is the
         * one way left for a price to move the button. It is the label's job to
         * be short; this makes it impossible for it not to be.
         */}
        <Button
          block
          style={{ whiteSpace: 'nowrap' }}
          onClick={quoteFailed ? retryQuote : buy}
          disabled={soldOut || noMethods || quoting || (!quote && !quoteFailed)}
          loading={busy}
        >
          {soldOut ? (
            'Нет в наличии'
          ) : noMethods ? (
            /* Not "Считаем цену…": nothing is being computed and nothing will
               be. The notice above says why. */
            'Оплата недоступна'
          ) : quoteFailed ? (
            'Рассчитать заново'
          ) : quote ? (
            /*
             * One element, and that is load-bearing rather than tidy.
             * `.button` is a flex row with `gap: var(--space-2)` and
             * `Button` wraps its children in `display: contents`, so
             * whatever is handed in becomes flex items of the button
             * itself — every sibling would gain 8px between it and the
             * next. The other three labels are single strings, which is
             * one anonymous item each; this has to be one item too, or
             * «Оплатить» and the figure drift apart and the bar's own
             * width moves with them.
             */
            <span>Оплатить {money(quote.quote.finalAmount)}</span>
          ) : (
            'Считаем цену…'
          )}
        </Button>
      </div>
    </div>
  );
}
